import React, { useState, useEffect } from 'react';
import { Dialog, DialogPanel, DialogTitle, Transition, TransitionChild, Listbox, ListboxButton, ListboxOptions, ListboxOption, Select, Field, Label, Button, Textarea, Input } from '@headlessui/react';
import { Fragment } from 'react';
import { FaTimes, FaSave, FaBuilding, FaGlobe, FaIndustry, FaChevronDown, FaCheck } from 'react-icons/fa';
import { getBaseUrl } from '../utils/tokenUtils';

const ProjectForm = ({ isOpen, onClose, onSubmit, project = null, isLoading = false }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    companyName: '',
    companyType: 'individual',
    industry: 'other',
    website: '',
    logo: '',
    logoFile: null,
    status: 'active'
  });

  const [errors, setErrors] = useState({});
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [logoPreview, setLogoPreview] = useState('');

  useEffect(() => {
    if (project) {
      console.log('ProjectForm - Edit mode, project:', project);
      console.log('ProjectForm - Project logo:', project.logo);
      
      setFormData({
        name: project.name || '',
        description: project.description || '',
        companyName: project.companyName || '',
        companyType: project.companyType || 'individual',
        industry: project.industry || 'other',
        website: project.website || '',
        logo: project.logo || '',
        logoFile: null,
        status: project.status || 'active'
      });
      // Set logo preview if project has logo
      if (project.logo) {
        // Handle different logo URL formats
        const logoUrl = project.logo.startsWith('http') 
          ? project.logo 
          : `${getBaseUrl()}${project.logo}`;
        console.log('ProjectForm - Setting logo preview:', logoUrl);
        setLogoPreview(logoUrl);
      } else {
        console.log('ProjectForm - No logo to preview');
        setLogoPreview('');
      }
    } else {
      console.log('ProjectForm - Create mode');
      setFormData({
        name: '',
        description: '',
        companyName: '',
        companyType: 'individual',
        industry: 'other',
        website: '',
        logo: '',
        logoFile: null,
        status: 'active'
      });
      setLogoPreview('');
    }
    setErrors({});
  }, [project]);

  // Reset form when dialog opens/closes
  useEffect(() => {
    if (isOpen) {
      setErrors({});
    }
  }, [isOpen]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (name.startsWith('settings.')) {
      const settingKey = name.split('.')[1];
      setFormData(prev => ({
        ...prev,
        settings: {
          ...prev.settings,
          [settingKey]: value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      }));
    }
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    console.log('ProjectForm - Logo file selected:', file);

    // File type check
    if (!file.type.startsWith('image/')) {
      setErrors(prev => ({ ...prev, logoFile: 'Please select a valid image file.' }));
      return;
    }

    // File size check (5MB)
    if (file.size > 5 * 1024 * 1024) {
      setErrors(prev => ({ ...prev, logoFile: 'File size must be less than 5MB.' }));
      return;
    }

    // Show local preview first
    const reader = new FileReader();
    reader.onload = (e) => {
      console.log('ProjectForm - Local preview set:', e.target.result);
      setLogoPreview(e.target.result);
    };
    reader.readAsDataURL(file);

    setErrors(prev => ({ ...prev, logoFile: '' }));
    
    // Store file for later upload (when form is submitted)
    setFormData(prev => ({
      ...prev,
      logoFile: file,
      logo: '' // Clear existing logo URL since we have a new file
    }));
  };

  const uploadLogoFile = async (file) => {
    const formData = new FormData();
    formData.append('icon', file);

    console.log('ProjectForm - Uploading file:', file.name);

    const response = await fetch('/api/upload/icon', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token')}`
      },
      body: formData
    });

    console.log('ProjectForm - Upload response status:', response.status);

    if (response.ok) {
      const result = await response.json();
      console.log('ProjectForm - Upload response:', result);
      // Server returns 'url' field, not 'iconUrl'
      return result.url;
    } else {
      const error = await response.json();
      console.error('ProjectForm - Upload error:', error);
      throw new Error(error.message || 'Logo upload failed');
    }
  };

  const uploadLogo = async (file) => {
    setUploadingLogo(true);
    
    try {
      const iconUrl = await uploadLogoFile(file);
      setFormData(prev => ({
        ...prev,
        logo: iconUrl
      }));
      // Update preview with server URL - handle different URL formats
      const serverLogoUrl = iconUrl.startsWith('http') 
        ? iconUrl 
        : `${getBaseUrl()}${iconUrl}`;
      setLogoPreview(serverLogoUrl);
      console.log('Logo uploaded successfully:', iconUrl);
      console.log('Logo preview URL:', serverLogoUrl);
    } catch (error) {
      console.error('Logo upload error:', error);
      setErrors(prev => ({ ...prev, logoFile: error.message || 'Error occurred while uploading logo.' }));
      // Don't clear preview on error - keep local preview
      console.log('Keeping local preview due to upload error');
    } finally {
      setUploadingLogo(false);
    }
  };

  const removeLogo = () => {
    console.log('ProjectForm - Removing logo');
    setFormData(prev => ({
      ...prev,
      logo: '',
      logoFile: null
    }));
    setLogoPreview('');
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Project name is required';
    }

    if (!formData.companyName.trim()) {
      newErrors.companyName = 'Company name is required';
    }

    if (formData.website && !isValidUrl(formData.website)) {
      newErrors.website = 'Please enter a valid URL';
    }

    // Don't validate logo URL if we have a logo file to upload
    console.log('ProjectForm validation - logo:', formData.logo);
    console.log('ProjectForm validation - logoFile:', formData.logoFile);
    console.log('ProjectForm validation - isValidUrl result:', formData.logo ? isValidUrl(formData.logo) : 'no logo');
    
    if (formData.logo && !formData.logoFile && !isValidUrl(formData.logo)) {
      console.log('ProjectForm validation - adding logo error');
      newErrors.logo = 'Please enter a valid URL';
    }

    console.log('ProjectForm validation - newErrors:', newErrors);
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const isValidUrl = (string) => {
    try {
      // Check if it's a valid absolute URL
      new URL(string);
      return true;
    } catch (_) {
      // Check if it's a valid relative path (starts with /)
      if (string && string.startsWith('/')) {
        return true;
      }
      return false;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    console.log('ProjectForm - handleSubmit called');
    console.log('ProjectForm - formData:', formData);
    
    const isValid = validateForm();
    console.log('ProjectForm - validation result:', isValid);
    console.log('ProjectForm - errors:', errors);
    
    if (isValid) {
      let submitData = { ...formData };
      
      console.log('ProjectForm submit - formData:', formData);
      console.log('ProjectForm submit - logoFile:', formData.logoFile);
      console.log('ProjectForm submit - current logo:', formData.logo);
      
      // If there's a logo file, upload it first
      if (formData.logoFile) {
        try {
          console.log('Uploading logo file:', formData.logoFile);
          setUploadingLogo(true);
          const uploadedLogoUrl = await uploadLogoFile(formData.logoFile);
          console.log('Logo uploaded successfully, URL:', uploadedLogoUrl);
          submitData.logo = uploadedLogoUrl;
          delete submitData.logoFile; // Remove file from submit data
        } catch (error) {
          console.error('Logo upload failed:', error);
          setErrors(prev => ({ ...prev, logoFile: 'Logo upload failed. Please try again.' }));
          setUploadingLogo(false);
          return;
        } finally {
          setUploadingLogo(false);
        }
      }
      
      console.log('ProjectForm submit - final submitData:', submitData);
      console.log('ProjectForm submit - final logo:', submitData.logo);
      console.log('ProjectForm submit - calling onSubmit with:', submitData);
      onSubmit(submitData);
    } else {
      console.log('ProjectForm - validation failed, not submitting');
    }
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <TransitionChild
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-50" />
        </TransitionChild>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <TransitionChild
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
                  <DialogPanel className="w-full max-w-2xl transform overflow-hidden rounded-2xl bg-black border border-gray-800 p-6 text-left align-middle shadow-xl transition-all">
                <div className="flex items-center justify-between mb-6">
                  <DialogTitle as="h3" className="text-xl font-semibold text-white">
                    {project ? 'Edit Project' : 'Create New Project'}
                  </DialogTitle>
                  <Button
                    onClick={onClose}
                    className="text-gray-400 hover:text-gray-300 transition-colors duration-200"
                  >
                    <FaTimes className="w-5 h-5" />
                  </Button>
                </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Proje Bilgileri */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-white flex items-center">
              <FaBuilding className="w-5 h-5 mr-2 text-indigo-400" />
              Project Information
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Field>
                  <Label className="block text-sm font-medium text-gray-300 mb-1">
                    Project Name *
                  </Label>
                  <Input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    className={`w-full px-4 py-3 bg-gray-900 border rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-white focus:border-white transition-colors duration-200 data-focus:ring-white data-focus:border-white ${
                      errors.name ? 'border-red-500' : 'border-gray-700'
                    }`}
                    placeholder="Enter project name"
                  />
                  {errors.name && <p className="mt-1 text-sm text-red-600">{errors.name}</p>}
                </Field>
              </div>

              <div>
                <Field>
                  <Label className="block text-sm font-medium text-gray-300 mb-1">
                    Company Name *
                  </Label>
                  <Input
                    type="text"
                    name="companyName"
                    value={formData.companyName}
                    onChange={handleChange}
                    className={`w-full px-4 py-3 bg-gray-900 border rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-white focus:border-white transition-colors duration-200 data-focus:ring-white data-focus:border-white ${
                      errors.companyName ? 'border-red-500' : 'border-gray-700'
                    }`}
                    placeholder="Enter company name"
                  />
                  {errors.companyName && <p className="mt-1 text-sm text-red-600">{errors.companyName}</p>}
                </Field>
              </div>
            </div>

            <div>
              <Field>
                <Label className="block text-sm font-medium text-gray-300 mb-1">
                  Description
                </Label>
                <Textarea
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  rows={3}
                  className="w-full px-4 py-3 bg-gray-900 border border-gray-700 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-white focus:border-white transition-colors duration-200 resize-none data-focus:ring-white data-focus:border-white"
                  placeholder="Enter project description"
                />
              </Field>
            </div>
          </div>

          {/* Şirket Bilgileri */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-white flex items-center">
              <FaIndustry className="w-5 h-5 mr-2 text-indigo-400" />
              Company Information
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Company Type
                </label>
                <Listbox value={formData.companyType} onChange={(value) => setFormData(prev => ({ ...prev, companyType: value }))}>
                  <div className="relative">
                        <ListboxButton className="relative w-full cursor-default rounded-lg bg-gray-900 border border-gray-700 py-3 pl-4 pr-10 text-left shadow-md focus:outline-none focus-visible:border-white focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-opacity-75 sm:text-sm">
                      <span className="block truncate text-white">{formData.companyType === 'individual' ? 'Individual' : formData.companyType === 'company' ? 'Company' : formData.companyType}</span>
                      <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                        <FaChevronDown className="h-5 w-5 text-gray-400" aria-hidden="true" />
                      </span>
                    </ListboxButton>
                    <Transition
                      as={Fragment}
                      leave="transition ease-in duration-100"
                      leaveFrom="opacity-100"
                      leaveTo="opacity-0"
                    >
                          <ListboxOptions className="absolute mt-1 max-h-60 w-full overflow-auto rounded-md bg-gray-900 border border-gray-700 py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm z-10">
                        {[
                              { value: 'individual', label: 'Individual' },
                              { value: 'company', label: 'Company' },
                          { value: 'LLC', label: 'LLC' },
                          { value: 'Corporation', label: 'Corporation' },
                          { value: 'Partnership', label: 'Partnership' },
                          { value: 'Sole Proprietorship', label: 'Sole Proprietorship' }
                        ].map((option) => (
                          <ListboxOption
                            key={option.value}
                            className={({ active }) =>
                              `relative cursor-default select-none py-2 pl-10 pr-4 ${
                                active ? 'bg-gray-800 text-white' : 'text-gray-300'
                              }`
                            }
                            value={option.value}
                          >
                            {({ selected }) => (
                              <>
                                <span className={`block truncate ${selected ? 'font-medium' : 'font-normal'}`}>
                                  {option.label}
                                </span>
                                {selected ? (
                                  <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-white">
                                    <FaCheck className="h-5 w-5" aria-hidden="true" />
                                  </span>
                                ) : null}
                              </>
                            )}
                          </ListboxOption>
                        ))}
                      </ListboxOptions>
                    </Transition>
                  </div>
                </Listbox>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Industry
                </label>
                <Listbox value={formData.industry} onChange={(value) => setFormData(prev => ({ ...prev, industry: value }))}>
                  <div className="relative">
                        <ListboxButton className="relative w-full cursor-default rounded-lg bg-gray-900 border border-gray-700 py-3 pl-4 pr-10 text-left shadow-md focus:outline-none focus-visible:border-white focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-opacity-75 sm:text-sm">
                      <span className="block truncate text-white">
                            {formData.industry === 'technology' ? 'Technology' :
                             formData.industry === 'healthcare' ? 'Healthcare' :
                             formData.industry === 'finance' ? 'Finance' :
                             formData.industry === 'retail' ? 'Retail' :
                             formData.industry === 'education' ? 'Education' :
                             formData.industry === 'entertainment' ? 'Entertainment' : 'Other'}
                      </span>
                      <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                        <FaChevronDown className="h-5 w-5 text-gray-400" aria-hidden="true" />
                      </span>
                    </ListboxButton>
                    <Transition
                      as={Fragment}
                      leave="transition ease-in duration-100"
                      leaveFrom="opacity-100"
                      leaveTo="opacity-0"
                    >
                          <ListboxOptions className="absolute mt-1 max-h-60 w-full overflow-auto rounded-md bg-gray-900 border border-gray-700 py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm z-10">
                        {[
                              { value: 'technology', label: 'Technology' },
                              { value: 'healthcare', label: 'Healthcare' },
                              { value: 'finance', label: 'Finance' },
                              { value: 'retail', label: 'Retail' },
                              { value: 'education', label: 'Education' },
                              { value: 'entertainment', label: 'Entertainment' },
                              { value: 'other', label: 'Other' }
                        ].map((option) => (
                          <ListboxOption
                            key={option.value}
                            className={({ active }) =>
                              `relative cursor-default select-none py-2 pl-10 pr-4 ${
                                active ? 'bg-gray-800 text-white' : 'text-gray-300'
                              }`
                            }
                            value={option.value}
                          >
                            {({ selected }) => (
                              <>
                                <span className={`block truncate ${selected ? 'font-medium' : 'font-normal'}`}>
                                  {option.label}
                                </span>
                                {selected ? (
                                  <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-white">
                                    <FaCheck className="h-5 w-5" aria-hidden="true" />
                                  </span>
                                ) : null}
                              </>
                            )}
                          </ListboxOption>
                        ))}
                      </ListboxOptions>
                    </Transition>
                  </div>
                </Listbox>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Status
                </label>
                <Listbox value={formData.status} onChange={(value) => setFormData(prev => ({ ...prev, status: value }))}>
                  <div className="relative">
                    <ListboxButton className="relative w-full cursor-default rounded-lg bg-gray-900 border border-gray-700 py-3 pl-4 pr-10 text-left shadow-md focus:outline-none focus-visible:border-white focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-opacity-75 sm:text-sm">
                      <span className="block truncate text-white">
                        {formData.status === 'active' ? 'Active' : 
                         formData.status === 'inactive' ? 'Inactive' : 
                         formData.status === 'archived' ? 'Archived' : formData.status}
                      </span>
                      <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                        <FaChevronDown className="h-5 w-5 text-gray-400" aria-hidden="true" />
                      </span>
                    </ListboxButton>
                    <Transition
                      as={Fragment}
                      leave="transition ease-in duration-100"
                      leaveFrom="opacity-100"
                      leaveTo="opacity-0"
                    >
                      <ListboxOptions className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-gray-900 border border-gray-700 py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
                        {[
                          { value: 'active', label: 'Active' },
                          { value: 'inactive', label: 'Inactive' },
                          { value: 'archived', label: 'Archived' }
                        ].map((option) => (
                          <ListboxOption
                            key={option.value}
                            className={({ active }) =>
                              `relative cursor-default select-none py-2 pl-10 pr-4 ${
                                active ? 'bg-gray-800 text-white' : 'text-gray-300'
                              }`
                            }
                            value={option.value}
                          >
                            {({ selected }) => (
                              <>
                                <span className={`block truncate ${selected ? 'font-medium' : 'font-normal'}`}>
                                  {option.label}
                                </span>
                                {selected ? (
                                  <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-white">
                                    <FaCheck className="h-5 w-5" aria-hidden="true" />
                                  </span>
                                ) : null}
                              </>
                            )}
                          </ListboxOption>
                        ))}
                      </ListboxOptions>
                    </Transition>
                  </div>
                </Listbox>
              </div>
            </div>
          </div>

          {/* Web Bilgileri */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-white flex items-center">
              <FaGlobe className="w-5 h-5 mr-2 text-indigo-400" />
              Web Information
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Field>
                  <Label className="block text-sm font-medium text-gray-300 mb-1">
                    Website URL
                  </Label>
                  <Input
                    type="url"
                    name="website"
                    value={formData.website}
                    onChange={handleChange}
                    className={`w-full px-4 py-3 bg-gray-900 border rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-white focus:border-white transition-colors duration-200 data-focus:ring-white data-focus:border-white ${
                      errors.website ? 'border-red-500' : 'border-gray-700'
                    }`}
                    placeholder="https://example.com"
                  />
                  {errors.website && <p className="mt-1 text-sm text-red-600">{errors.website}</p>}
                </Field>
              </div>

              <div>
                <Field>
                  <Label className="block text-sm font-medium text-gray-300 mb-1">
                    Project Logo
                  </Label>
                  <div className="space-y-3">
                    <div className="relative">
                      {logoPreview ? (
                        <div className="relative w-32 h-32 mx-auto">
                          <img 
                            src={logoPreview.startsWith('data:') ? logoPreview : 
                                 logoPreview.startsWith('http') ? logoPreview : 
                                 `${getBaseUrl()}${logoPreview}`}
                            alt="Logo preview" 
                            className="w-full h-full object-cover rounded-lg border border-gray-700"
                            onError={(e) => {
                              console.error('Logo preview error:', e);
                              setLogoPreview('');
                            }}
                          />
                          {/* Action buttons overlay */}
                          <div className="absolute top-2 right-2 flex space-x-1">
                            <Button
                              type="button"
                              onClick={removeLogo}
                              className="p-1.5 bg-red-600 text-white rounded-full hover:bg-red-700 transition-colors duration-200"
                              title="Remove logo"
                            >
                              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                              </svg>
                            </Button>
                            <label className="p-1.5 bg-gray-700 text-white rounded-full hover:bg-gray-600 transition-colors duration-200 cursor-pointer">
                              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                              </svg>
                              <Input 
                                type="file" 
                                className="hidden" 
                                accept="image/*"
                                onChange={handleLogoUpload}
                                disabled={uploadingLogo}
                              />
                            </label>
                          </div>
                          {uploadingLogo && (
                            <div className="absolute inset-0 bg-black bg-opacity-50 rounded-lg flex items-center justify-center">
                              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-white"></div>
                            </div>
                          )}
                        </div>
                      ) : (
                        <div className="flex items-center justify-center w-full">
                          <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-gray-700 border-dashed rounded-lg cursor-pointer bg-gray-900 hover:bg-gray-800">
                            <div className="flex flex-col items-center justify-center pt-5 pb-6">
                              {uploadingLogo ? (
                                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
                              ) : (
                                <>
                                  <svg className="w-8 h-8 mb-4 text-gray-400" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 20 16">
                                    <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 13h3a3 3 0 0 0 0-6h-.025A5.56 5.56 0 0 0 16 6.5 5.5 5.5 0 0 0 5.207 5.021C5.137 5.017 5.071 5 5 5a4 4 0 0 0 0 8h2.167M10 15V6m0 0L8 8m2-2 2 2"/>
                                  </svg>
                                  <p className="mb-2 text-sm text-gray-400">
                                    <span className="font-semibold">Click to upload logo</span>
                                  </p>
                                  <p className="text-xs text-gray-500">PNG, JPG, GIF (MAX. 5MB)</p>
                                </>
                              )}
                            </div>
                            <Input 
                              type="file" 
                              className="hidden" 
                              accept="image/*"
                              onChange={handleLogoUpload}
                              disabled={uploadingLogo}
                            />
                          </label>
                        </div>
                      )}
                    </div>
                    
                    {errors.logoFile && (
                      <p className="text-sm text-red-600">{errors.logoFile}</p>
                    )}
                  </div>
                </Field>
              </div>
            </div>
          </div>


          {/* Actions */}
          <div className="flex items-center justify-end space-x-3 pt-6 border-t border-gray-800">
            <Button
              type="button"
              onClick={onClose}
              className="px-6 py-3 text-sm font-medium text-gray-300 bg-gray-800 border border-gray-700 rounded-xl hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-white transition-colors duration-200 data-hover:bg-gray-700 data-focus:ring-white"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isLoading}
              className="flex items-center px-6 py-3 text-sm font-medium text-black bg-white border border-transparent rounded-xl hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-white disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200 data-hover:bg-gray-100 data-focus:ring-white"
            >
              {isLoading ? (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-black mr-2"></div>
              ) : (
                <FaSave className="w-4 h-4 mr-2" />
              )}
              {project ? 'Update' : 'Create'}
            </Button>
          </div>
        </form>
              </DialogPanel>
            </TransitionChild>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default ProjectForm;
